import { ref } from "vue";
import { defineStore } from "pinia";
import { strippedHtml } from "@/utils/Formatters";
import { scrollToFirstError } from "@/utils/FormUtils";

export const useSnackStore = defineStore("snack", () => {
  const defaultTimeout = 3000;
  const queue = ref<ISnackItem[]>([]);
  let nextKey = 0;

  const recentlyShown = new Map<string, ISnackItem>();

  function trackAsShown(item: ISnackItem) {
    clearTimeout(item.forgetTimer);
    recentlyShown.set(item.signature, item);
    item.forgetTimer = setTimeout(() => {
      if (recentlyShown.get(item.signature) === item) {
        recentlyShown.delete(item.signature);
      }
    }, item.timeout + dedupeGrace);
  }

  function showSnack(showSnack: IShowSnack, scrollToError: boolean = true) {
    if (showSnack.color === "error") {
      console.error("Snack:", showSnack.message);
    } else {
      console.info("Snack:", showSnack.message);
    }

    const message = showSnack.message || "";
    const timeout = showSnack.timeout || calcTimeout(String(message));
    const priority = showSnack.position === "priority";
    const signature = signatureOf(showSnack);

    const duplicate = recentlyShown.get(signature);
    if (duplicate) {
      if (queue.value.includes(duplicate)) {
        duplicate.timeout = Math.max(duplicate.timeout, timeout);
      }
      if (showSnack.color === "error" && scrollToError) {
        scrollToFirstError();
      }
      return;
    }

    const item: ISnackItem = {
      key: nextKey++,
      signature,
      message,
      timeout,
      color: showSnack.color || "notice",
      priority,
    };

    if (priority) {
      const firstCenterIndex = queue.value.findIndex((i) => !i.priority);
      if (firstCenterIndex === -1) {
        queue.value.push(item);
      } else {
        queue.value.splice(firstCenterIndex, 0, item);
      }
    } else {
      queue.value.push(item);
    }

    trackAsShown(item);

    if (showSnack.color === "error" && scrollToError) {
      scrollToFirstError();
    }
  }

  const convertMessage = (message: unknown): string | string[] =>
    Array.isArray(message) ? message.map(String) : String(message);

  function showError(message: unknown, scrollToFirstError: boolean = true) {
    showSnack(
      {
        message: convertMessage(message),
        color: "error",
      },
      scrollToFirstError
    );
  }

  function showSuccess(message: unknown) {
    showSnack({
      message: convertMessage(message),
      color: "success",
    });
  }

  function showPriorityError(message: unknown, scrollToFirstError: boolean = true) {
    showSnack(
      {
        message: convertMessage(message),
        color: "error",
        position: "priority",
        timeout: calcTimeout(String(message)) + 2000,
      },
      scrollToFirstError
    );
  }

  function calcTimeout(message: string): number {
    const plainTextMessage = strippedHtml(message);
    const readingSpeed = 25;
    const estimatedTime = (plainTextMessage.length / readingSpeed) * 1000;
    return Math.max(defaultTimeout, estimatedTime);
  }

  return {
    queue,
    showSnack,
    showError,
    showSuccess,
    showPriorityError,
  };
});

const dedupeGrace = 1000;

const UNIT_SEPARATOR = "\u001F";

function signatureOf(showSnack: IShowSnack): string {
  const message = showSnack.message || "";
  const messagePart = Array.isArray(message) ? message.join(UNIT_SEPARATOR) : String(message);
  return [showSnack.color || "notice", showSnack.position || "center", messagePart].join(
    UNIT_SEPARATOR
  );
}

export type TSnackPosition = "priority" | "center";

export interface ISnackItem {
  key: number;
  signature: string;
  message: string | string[];
  timeout: number;
  color: string;
  priority: boolean;
  forgetTimer?: ReturnType<typeof setTimeout>;
}

export interface IShowSnack {
  message: string | string[];
  timeout?: number;
  color: string;
  position?: TSnackPosition;
}
